import React, { useState } from "react";
import { Search, Sparkles, ArrowRight, HelpCircle, CheckCircle, Lightbulb, Compass, Filter, RefreshCw, AlertTriangle } from "lucide-react";
import { AI_FEATURES_ENABLED } from "../../config/featureFlags";

interface JobSearchAgentToolProps {
  apiFetch: (url: string, init?: RequestInit) => Promise<Response>;
  onLaunchSearch: (params: { keyword: string; location: string; radiusMiles: number }) => void;
  userPreferences?: any;
  savedEmployers?: any[];
  theme?: "light" | "dark";
  providerHealth?: { status: string; outageReason?: string | null };
  onProviderError?: (err: any) => void;
}

export default function JobSearchAgentTool({
  apiFetch,
  onLaunchSearch,
  userPreferences,
  savedEmployers = [],
  theme = "light",
  providerHealth,
  onProviderError
}: JobSearchAgentToolProps) {
  const isDark = theme === "dark";
  const isCircuitOpen = providerHealth?.status === "outage";

  const [queryGoal, setQueryGoal] = useState("");
  const [desiredTitles, setDesiredTitles] = useState(userPreferences?.desiredTitles || "");
  const [experienceLevel, setExperienceLevel] = useState("Entry to Mid-level");
  const [skillsText, setSkillsText] = useState(
    Array.isArray(userPreferences?.skills) ? userPreferences.skills.join(", ") : ""
  );
  const [preferredIndustries, setPreferredIndustries] = useState("");
  const [travelDistance, setTravelDistance] = useState(25);
  const [workType, setWorkType] = useState("any");
  const [locationText, setLocationText] = useState(userPreferences?.location || "Local Area");

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<any | null>(null);

  const handleRunAgent = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (isLoading || isCircuitOpen) return;

    if (!AI_FEATURES_ENABLED) {
      setError("AI features are currently disabled.");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const res = await apiFetch("/api/ai/job-search-agent", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          queryGoal: queryGoal || desiredTitles || "Find suitable local job opportunities",
          experienceLevel,
          skills: skillsText.split(",").map((s) => s.trim()).filter(Boolean),
          preferredIndustries: preferredIndustries.split(",").map((i) => i.trim()).filter(Boolean),
          travelDistance: Number(travelDistance),
          workType,
          userPreferences: { ...userPreferences, location: locationText },
          savedEmployers
        })
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data.success) {
        if (data.providerError || res.status === 503 || res.status === 502) {
          if (onProviderError) onProviderError(data.providerError || data);
        }
        throw new Error(data.error || "AI tools are temporarily unavailable.\n\nYour request was not used, and no AI usage was consumed.\nPlease return later.");
      }

      setResult(data.data);
    } catch (err: any) {
      console.error("Job Search Agent Error:", err);
      setError(err.message || "AI tools are temporarily unavailable.\n\nYour request was not used, and no AI usage was consumed.\nPlease return later.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6 text-left">
      {/* Header card */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 md:p-6 shadow-xs">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2.5 bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 rounded-xl">
            <Compass className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-lg font-black text-slate-900 dark:text-white tracking-tight">AI Job Search Agent</h2>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Interactive strategy builder that formulates optimized queries, industries, and titles for your local search.
            </p>
          </div>
        </div>

        {/* Input Form */}
        <form onSubmit={handleRunAgent} className="mt-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                Your Search Goal / What roles do you want?
              </label>
              <input
                type="text"
                disabled={isLoading || isCircuitOpen}
                value={queryGoal}
                onChange={(e) => setQueryGoal(e.target.value)}
                placeholder={isCircuitOpen ? "AI tools are temporarily down..." : "e.g. Entry-level office coordinator, dental assistant, or customer service"}
                className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-xs text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60 disabled:cursor-not-allowed"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                Key Skills & Strengths (comma-separated)
              </label>
              <input
                type="text"
                disabled={isLoading || isCircuitOpen}
                value={skillsText}
                onChange={(e) => setSkillsText(e.target.value)}
                placeholder={isCircuitOpen ? "AI tools are temporarily down..." : "e.g. Communication, scheduling, Excel, phone support, bilingual"}
                className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-xs text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60 disabled:cursor-not-allowed"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                Experience Level
              </label>
              <select
                disabled={isLoading || isCircuitOpen}
                value={experienceLevel}
                onChange={(e) => setExperienceLevel(e.target.value)}
                className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-xs text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <option value="Entry-level / First job">Entry-level / First job</option>
                <option value="Entry to Mid-level">Entry to Mid-level (1-3 yrs)</option>
                <option value="Mid-level Experienced">Mid-level Experienced (3-5 yrs)</option>
                <option value="Career Changer / Transferable Skills">Career Changer / Transferable Skills</option>
                <option value="Senior / Specialist">Senior / Specialist</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                Target Location or ZIP Code
              </label>
              <input
                type="text"
                disabled={isLoading || isCircuitOpen}
                value={locationText}
                onChange={(e) => setLocationText(e.target.value)}
                placeholder={isCircuitOpen ? "AI tools are temporarily down..." : "e.g. Chicago, IL or 60601"}
                className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-xs text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60 disabled:cursor-not-allowed"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                Max Commute Distance: <span className="text-indigo-600 font-extrabold">{travelDistance} miles</span>
              </label>
              <input
                type="range"
                disabled={isLoading || isCircuitOpen}
                min="5"
                max="50"
                step="5"
                value={travelDistance}
                onChange={(e) => setTravelDistance(Number(e.target.value))}
                className="w-full accent-indigo-600 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 mb-1">
                Work Preference
              </label>
              <select
                disabled={isLoading || isCircuitOpen}
                value={workType}
                onChange={(e) => setWorkType(e.target.value)}
                className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-xs text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <option value="any">Any Work Style</option>
                <option value="in_person">In-Person Local</option>
                <option value="hybrid">Hybrid</option>
                <option value="remote">Remote / Work from home</option>
              </select>
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading || isCircuitOpen}
            className="w-full sm:w-auto px-6 py-3 bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-400 dark:disabled:bg-slate-700 disabled:cursor-not-allowed text-white font-extrabold text-xs rounded-xl transition shadow-xs flex items-center justify-center gap-2 cursor-pointer"
          >
            {isLoading ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                <span>Formulating AI Search Strategy...</span>
              </>
            ) : isCircuitOpen ? (
              <>
                <AlertTriangle className="w-4 h-4 text-amber-300" />
                <span>AI Service Temporarily Unavailable</span>
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 text-amber-300" />
                <span>Generate Search Recommendations</span>
              </>
            )}
          </button>
        </form>
      </div>

      {error && (
        <div className="p-4 bg-amber-50 dark:bg-amber-950/50 border border-amber-300 dark:border-amber-800 rounded-2xl flex items-start gap-3 text-xs text-amber-950 dark:text-amber-100 shadow-xs">
          <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
          <div className="space-y-1 min-w-0 flex-1">
            <p className="font-bold text-amber-900 dark:text-amber-200">AI Service Notice</p>
            <p className="leading-relaxed whitespace-pre-line">{error}</p>
          </div>
        </div>
      )}

      {/* AI Output Display */}
      {result && (
        <div className="space-y-6">
          {/* Follow-up questions if details missing */}
          {result.followUpQuestions && result.followUpQuestions.length > 0 && (
            <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/50 rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-2 text-amber-900 dark:text-amber-300 font-extrabold text-xs uppercase tracking-wider">
                <HelpCircle className="w-4 h-4 text-amber-600" />
                <span>Refine Strategy with Follow-up Details</span>
              </div>
              <p className="text-xs text-amber-800 dark:text-amber-200 mb-3">
                To sharpen these recommendations further, consider answering:
              </p>
              <ul className="space-y-1.5 text-xs text-amber-900 dark:text-amber-100 font-medium">
                {result.followUpQuestions.map((q: string, idx: number) => (
                  <li key={idx} className="flex items-start gap-2">
                    <span className="text-amber-500 font-black">•</span>
                    <span>{q}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Recommended Next Action Box */}
          <div className={`${
            isDark
              ? "bg-slate-900 border-slate-800 text-white"
              : "bg-blue-50/80 border-blue-200 text-slate-900"
          } rounded-2xl p-6 shadow-sm border flex flex-col md:flex-row justify-between items-start md:items-center gap-4`}>
            <div className="space-y-1">
              <span className={`text-[10px] uppercase font-black tracking-wider ${isDark ? "text-indigo-400" : "text-blue-700"} block`}>
                Recommended Action
              </span>
              <h3 className={`text-base font-extrabold ${isDark ? "text-white" : "text-slate-900"}`}>{result.nextActionSummary}</h3>
              <p className={`text-xs ${isDark ? "text-slate-300" : "text-slate-600"}`}>
                Launch search using keyword: <span className={`font-bold ${isDark ? "text-white" : "text-slate-900"}`}>"{result.launchParameters?.keyword}"</span> in <span className={`font-bold ${isDark ? "text-white" : "text-slate-900"}`}>{result.launchParameters?.location || locationText}</span> ({result.launchParameters?.radiusMiles} miles)
              </p>
            </div>

            <button
              type="button"
              onClick={() =>
                onLaunchSearch({
                  keyword: result.launchParameters?.keyword || "office",
                  location: result.launchParameters?.location || locationText,
                  radiusMiles: result.launchParameters?.radiusMiles || travelDistance
                })
              }
              className="px-5 py-3 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black text-xs rounded-xl shadow-md transition flex items-center gap-2 shrink-0 cursor-pointer"
            >
              <Search className="w-4 h-4" />
              <span>Apply Parameters & Launch Search</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          {/* Recommendations Breakdown Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Recommended & Alternative Job Titles */}
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 space-y-3">
              <h4 className="text-xs font-black uppercase text-slate-500 tracking-wider flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-500" /> Recommended Job Titles
              </h4>
              <div className="flex flex-wrap gap-2">
                {result.recommendedTitles?.map((title: string, i: number) => (
                  <span
                    key={i}
                    className="px-3 py-1.5 bg-indigo-50 dark:bg-indigo-950/60 border border-indigo-200 dark:border-indigo-800 text-indigo-900 dark:text-indigo-200 font-bold text-xs rounded-lg"
                  >
                    {title}
                  </span>
                ))}
              </div>

              {result.alternativeTitles && result.alternativeTitles.length > 0 && (
                <div className="pt-2">
                  <h5 className="text-[11px] font-bold text-slate-600 dark:text-slate-400 mb-1.5">
                    Alternative Titles You May Have Overlooked:
                  </h5>
                  <div className="flex flex-wrap gap-1.5">
                    {result.alternativeTitles.map((alt: string, i: number) => (
                      <span key={i} className="px-2.5 py-1 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-xs rounded-md">
                        {alt}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Target Employer Types & Industries */}
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 space-y-3">
              <h4 className="text-xs font-black uppercase text-slate-500 tracking-wider flex items-center gap-2">
                <Filter className="w-4 h-4 text-indigo-500" /> Target Employer Types & Industries
              </h4>
              <div>
                <span className="text-[11px] font-bold text-slate-600 dark:text-slate-400 block mb-1">Recommended Employer Types:</span>
                <div className="flex flex-wrap gap-1.5">
                  {result.recommendedEmployerTypes?.map((emp: string, i: number) => (
                    <span key={i} className="px-2.5 py-1 bg-violet-50 dark:bg-violet-950/60 border border-violet-200 dark:border-violet-800 text-violet-900 dark:text-violet-200 font-semibold text-xs rounded-md">
                      {emp}
                    </span>
                  ))}
                </div>
              </div>

              {result.recommendedIndustries && result.recommendedIndustries.length > 0 && (
                <div className="pt-1">
                  <span className="text-[11px] font-bold text-slate-600 dark:text-slate-400 block mb-1">Recommended Industries:</span>
                  <div className="flex flex-wrap gap-1.5">
                    {result.recommendedIndustries.map((ind: string, i: number) => (
                      <span key={i} className="px-2.5 py-1 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-xs rounded-md">
                        {ind}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Rationale & Unexplored Directions */}
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 space-y-4">
            <h4 className="text-xs font-black uppercase text-slate-500 tracking-wider flex items-center gap-2">
              <Lightbulb className="w-4 h-4 text-amber-500" /> Strategy Rationale & Transferable Matches
            </h4>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {result.rationales?.map((r: { title: string; reason: string }, idx: number) => (
                <div key={idx} className="p-3 bg-slate-50 dark:bg-slate-800/60 rounded-xl border border-slate-100 dark:border-slate-800">
                  <span className="font-extrabold text-xs text-slate-900 dark:text-white block">{r.title}</span>
                  <p className="text-xs text-slate-600 dark:text-slate-300 mt-1">{r.reason}</p>
                </div>
              ))}
            </div>

            {result.transferableSkillRoles && result.transferableSkillRoles.length > 0 && (
              <div className="border-t border-slate-100 dark:border-slate-800 pt-3">
                <span className="text-xs font-bold text-slate-700 dark:text-slate-300 block mb-1">
                  Roles Matching Your Transferable Skills:
                </span>
                <p className="text-xs text-slate-600 dark:text-slate-400">
                  {result.transferableSkillRoles.join(" • ")}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
